import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Globe, Phone, List, Map, Heart, ArrowUpDown } from 'lucide-react';
import { fairApi } from '../services/api';
import { Fair } from '../types';
import FairFilters from './FairFilters';
import MobileFairFilters from './MobileFairFilters';
import MapView from './MapView';
import AdPlacement from './AdPlacement';
import { useAdConfig } from './adConfig';
import { getCurrentLocation, sortByDistance, formatDistance, calculateDistance, Location } from '../utils/geolocation';
import SEO from './SEO';
import Pagination from './Pagination';
import { getFairStatus, getTodayString, formatDateRange, parseDateOnly } from '../utils/fairStatus';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import FollowerCountBadge from './FollowerCountBadge'; // Issue #1313: Fair following feature
import FoundingMarketBadge from './FoundingMarketBadge'; // Issue #1318: Founding market badge
import SeekingManagerBadge from './SeekingManagerBadge'; // Issue #1451: Seeking manager badge for seeded fairs
import { useFeatureFlag } from '../hooks/useFeatureFlag'; // Feature flag support

// Constants for conversions (Issue #1197 Phase 2)
const KM_TO_MILES = 0.621371;

// Type for Fair with cached distance (Issue #1197 Phase 2)
type FairWithDistance = Fair & { cachedDistanceMiles?: number };

export default function FairList() {
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchName, setSearchName] = useState('');
  const [selectedAttendantStatus, setSelectedAttendantStatus] = useState('');
  const [dateFilter, setDateFilter] = useState<'upcoming' | 'all' | 'past'>('upcoming');
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<Location | undefined>();
  const [, setLocationLoading] = useState(false);
  const [, setLocationError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  // Issue #1197: Sort controls for customer fair discovery
  const [sortBy, setSortBy] = useState<'date' | 'distance' | 'name' | 'featured'>('featured');
  const [savedFairs, setSavedFairs] = useState<Set<number>>(new Set());
  // Phase 2: Advanced filters (Issue #1197)
  const [radiusMiles, setRadiusMiles] = useState<number | null>(null);
  const [maxAdmissionPrice, setMaxAdmissionPrice] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Pagination state
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('page_size')) || 25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const { config: adConfig } = useAdConfig();
  const { isEnabled: isFairFollowingEnabled } = useFeatureFlag('fair_following');

  useEffect(() => {
    const loadFairs = async () => {
      try {
        setLoading(true);

        // Calculate date range based on filter
        let startDate: string | undefined;
        let endDate: string | undefined;

        if (dateFilter === 'upcoming') {
          // Show fairs from today onwards
          startDate = getTodayString();
        } else if (dateFilter === 'past') {
          // Show fairs before today
          endDate = getTodayString();
        }
        // For 'all', leave both undefined to show all fairs

        const response = await fairApi.searchPaginated(
          selectedState || undefined,
          selectedCity || undefined,
          undefined, // product_category removed (Issue #130)
          currentPage,
          pageSize,
          startDate,
          endDate
        );
        setFairs(response.items);
        setTotalItems(response.total);
        setTotalPages(response.total_pages);
        setError(null);
      } catch (err: unknown) {
        console.error('Failed to load artist fairs:', err);
        setError('Failed to load artist fairs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadFairs();
  }, [selectedState, selectedCity, currentPage, pageSize, dateFilter]);

  // Performance optimization: Pre-calculate distances once when userLocation changes (Issue #1197 Phase 2)
  // This avoids expensive Haversine calculations on every render (50-80% performance improvement)
  const fairsWithDistance = useMemo<FairWithDistance[]>(() => {
    if (!userLocation) return fairs;

    return fairs.map(fair => {
      if (fair.address?.coordinates?.lat && fair.address?.coordinates?.lng) {
        const distanceKm = calculateDistance(
          userLocation,
          { latitude: fair.address.coordinates.lat, longitude: fair.address.coordinates.lng }
        );
        return { ...fair, cachedDistanceMiles: distanceKm * KM_TO_MILES };
      }
      return fair;
    });
  }, [fairs, userLocation]);

  // Memoize filtered fairs to avoid recalculating on every render (Issue #1197 Phase 2)
  const filteredFairs = useMemo(() => {
    return fairsWithDistance.filter(stand => {
      const nameMatch = searchName === '' ||
        stand.name.toLowerCase().includes(searchName.toLowerCase());

      const attendantMatch = selectedAttendantStatus === '' ||
        (selectedAttendantStatus === 'attended' && stand.has_attendant === true) ||
        (selectedAttendantStatus === 'self-serve' && stand.has_attendant === false);

      // Filter out draft fairs from public view (Issue #217)
      // Draft fairs are not publicly visible until payment is completed or fee is waived
      const isDraftExcluded = !stand.is_draft;

      // Phase 2: Radius filter (Issue #1197)
      // Only apply if user has location AND radius is selected
      // Use pre-calculated distance for better performance
      let radiusMatch = true;
      if (radiusMiles !== null && userLocation) {
        if ('cachedDistanceMiles' in stand && typeof stand.cachedDistanceMiles === 'number') {
          radiusMatch = stand.cachedDistanceMiles <= radiusMiles;
        } else {
          // Exclude fairs without coordinates when radius filter is active
          radiusMatch = false;
        }
      }

      // Phase 2: Admission price filter (Issue #1197)
      // Treat null/undefined as free ($0.00)
      // Handle invalid Decimal values gracefully
      let priceMatch = true;
      if (maxAdmissionPrice !== null) {
        const price = stand.attendee_entry_fee ? parseFloat(stand.attendee_entry_fee) : 0;
        // Gracefully handle invalid values - if parsing fails, treat as valid (don't hide fair)
        priceMatch = isNaN(price) ? true : price <= maxAdmissionPrice;
      }

      // Phase 2: Category filter (Issue #1197)
      // Check if fair's fair_categories array includes selectedCategory (case-insensitive)
      // Exclude fairs without categories when category filter is active
      let categoryMatch = true;
      if (selectedCategory !== '') {
        if (stand.fair_categories && stand.fair_categories.length > 0) {
          categoryMatch = stand.fair_categories.some(
            cat => cat.toLowerCase() === selectedCategory.toLowerCase()
          );
        } else {
          // Exclude fairs without categories when category filter is active
          categoryMatch = false;
        }
      }

      return nameMatch && attendantMatch && isDraftExcluded && radiusMatch && priceMatch && categoryMatch;
    });
  }, [fairsWithDistance, searchName, selectedAttendantStatus, radiusMiles, maxAdmissionPrice, selectedCategory, userLocation]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSearchParams({ page: newPage.toString(), page_size: pageSize.toString() });
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 when page size changes
    setSearchParams({ page: '1', page_size: newPageSize.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Issue #1197: Save/unsave fair functionality
  const toggleSaveFair = useCallback((fairId: number, event: React.MouseEvent) => {
    event.preventDefault(); // Prevent navigation when clicking heart
    event.stopPropagation();

    setSavedFairs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fairId)) {
        newSet.delete(fairId);
      } else {
        newSet.add(fairId);
      }
      // Persist to localStorage
      localStorage.setItem('savedFairs', JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  }, []);

  // Load saved fairs from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savedFairs');
      if (saved) {
        setSavedFairs(new Set(JSON.parse(saved)));
      }
    } catch (err) {
      console.error('Failed to load saved fairs:', err);
    }
  }, []);

  // Reset to page 1 when client-side filters change (Issue #1197 Phase 2)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      setSearchParams({ page: '1', page_size: pageSize.toString() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Don't include currentPage in dependencies to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusMiles, maxAdmissionPrice, selectedCategory, searchName, selectedAttendantStatus, pageSize, setSearchParams]);

  const handleLocationRequest = useCallback(async () => {
    setLocationLoading(true);
    setLocationError(null);

    try {
      const location = await getCurrentLocation();
      setUserLocation(location);
      // Location obtained successfully - don't log coordinates for privacy
    } catch (error: unknown) {
      setLocationError(extractApiError(error).message);
      console.error('Failed to get location:', error);
    } finally {
      setLocationLoading(false);
    }
  }, []);

  // Memoize fair status calculations (Issue #39 optimization)
  // Calculate once when filteredFairs changes, not on every render
  const fairsWithStatus = useMemo(() => {
    return filteredFairs.map(fair => ({
      ...fair,
      statusInfo: getFairStatus(fair.start_date, fair.end_date)
    }));
  }, [filteredFairs]);

  // Issue #1197: User-controlled sorting with multiple options
  const sortedFairs = useMemo(() => {
    let sorted = [...fairsWithStatus];

    switch (sortBy) {
      case 'featured': {
        // Featured first, then regular (original behavior)
        const featured = sorted.filter(f => f.is_featured);
        const regular = sorted.filter(f => !f.is_featured);

        // Sort each group by distance if location available
        const sortedFeatured = userLocation ? sortByDistance(
          featured.map(f => ({
            ...f,
            address: {
              ...f.address,
              latitude: f.address?.coordinates?.lat || 0,
              longitude: f.address?.coordinates?.lng || 0
            }
          })),
          userLocation
        ) : featured;

        const sortedRegular = userLocation ? sortByDistance(
          regular.map(f => ({
            ...f,
            address: {
              ...f.address,
              latitude: f.address?.coordinates?.lat || 0,
              longitude: f.address?.coordinates?.lng || 0
            }
          })),
          userLocation
        ) : regular;

        sorted = [...sortedFeatured, ...sortedRegular];
        break;
      }

      case 'distance': {
        // Sort by distance from user location
        if (userLocation) {
          sorted = sortByDistance(
            sorted.map(f => ({
              ...f,
              address: {
                ...f.address,
                latitude: f.address?.coordinates?.lat || 0,
                longitude: f.address?.coordinates?.lng || 0
              }
            })),
            userLocation
          );
        }
        break;
      }

      case 'date': {
        // Sort by start date (earliest first for upcoming, latest first for past)
        sorted.sort((a, b) => {
          const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
          const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
          return dateFilter === 'past' ? dateB - dateA : dateA - dateB;
        });
        break;
      }

      case 'name': {
        // Sort alphabetically by name
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      }

      default:
        break;
    }

    return sorted;
  }, [fairsWithStatus, sortBy, userLocation, dateFilter]);

  // Keep legacy variables for backward compatibility
  const featuredFairs = sortedFairs.filter(f => f.is_featured);
  const regularFairs = sortedFairs.filter(f => !f.is_featured);

  // Create artist fairs list with ads interspersed
  // Renders ALL artist fairs (featured first, then regular) with appropriate styling
  const createFairsWithAds = () => {
    const items: (JSX.Element | null)[] = [];
    // Process all artist fairs: featured + regular (already sorted correctly)
    const allFairs = [...featuredFairs, ...regularFairs];

    allFairs.forEach((stand, index) => {
      const isFeatured = stand.is_featured;

      // Add artist fair with featured styling if applicable
      items.push(
        <Link
          key={stand.id}
          to={`/fair/${stand.id}`}
          className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow block ${
            isFeatured ? 'ring-2 ring-primary' : ''
          }`}
        >
          <div className="h-32 lg:h-40 overflow-hidden relative">
            <img
              src={stand.image}
              alt={stand.name}
              className="w-full h-full object-cover"
            />
            {/* Badge container for featured and/or founding market badges */}
            <div className="absolute top-2 right-2 flex flex-col gap-2 items-end">
              {isFeatured && (
                <div className="bg-dusk-rose text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center space-x-1">
                  <span>⭐</span>
                  <span>FEATURED</span>
                </div>
              )}
              {stand.is_founding_market && (
                <FoundingMarketBadge size="small" />
              )}
              {stand.submitted_by == null && (
                <SeekingManagerBadge size="small" />
              )}
            </div>
            {/* Save Fair Button (Issue #1197) - Hidden when fair_following feature is disabled */}
            {isFairFollowingEnabled && (
              <button
                onClick={(e) => toggleSaveFair(stand.id, e)}
                className={`absolute top-2 left-2 p-2 rounded-full shadow-lg transition-all ${
                  savedFairs.has(stand.id)
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={savedFairs.has(stand.id) ? 'Unsave fair' : 'Save fair'}
                data-testid={`save-fair-${stand.id}`}
              >
                <Heart
                  className="h-4 w-4"
                  fill={savedFairs.has(stand.id) ? 'currentColor' : 'none'}
                />
              </button>
            )}
          </div>
          <div className="p-3">
            <h2 className="font-semibold text-primary-dark mb-1 text-base lg:text-lg">{stand.name}</h2>
            <p className="text-gray-600 mb-2 text-sm line-clamp-2">
              {stand.description || 'No description available'}
            </p>

            {/* Categories */}
            {stand.categories && stand.categories.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {stand.categories.map((category, idx) => (
                  <span
                    key={idx}
                    className="inline-block px-2 py-0.5 text-xs font-medium bg-secondary-light text-primary-dark rounded"
                  >
                    {category}
                  </span>
                ))}
              </div>
            )}

            {/* Multi-Day Event Info (Issue #11) */}
            {stand.start_date && stand.end_date && (
              <div className="mb-2 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Date Range */}
                  <span className="text-xs text-gray-700 font-medium">
                    📅 {formatDateRange(stand.start_date, stand.end_date)}
                  </span>

                  {/* Fair Status Badge (Issue #39 - using memoized statusInfo) */}
                  {stand.statusInfo && (() => {
                    if (stand.statusInfo.status === 'ongoing') {
                      return (
                        <span className="inline-block px-2 py-0.5 text-xs font-bold bg-secondary-light text-primary-dark rounded animate-pulse">
                          🎪 Happening Now
                        </span>
                      );
                    } else if (stand.statusInfo.status === 'upcoming' && stand.statusInfo.daysUntil !== undefined) {
                      return (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-moonbone text-ink rounded">
                          📅 Coming in {stand.statusInfo.daysUntil} day{stand.statusInfo.daysUntil !== 1 ? 's' : ''}
                        </span>
                      );
                    }
                    return null;
                  })()}

                  {/* Multi-Day Badge */}
                  {(() => {
                    const start = parseDateOnly(stand.start_date);
                    const end = parseDateOnly(stand.end_date);
                    if (!start || !end) return null;
                    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    return days > 1 ? (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                        {days}-Day Event
                      </span>
                    ) : null;
                  })()}
                </div>

                {/* Slot Availability */}
                {stand.total_slots !== undefined && stand.total_slots > 0 && (
                  <div className="text-xs text-gray-600">
                    🏪 {stand.total_slots} vendor booth{stand.total_slots !== 1 ? 's' : ''} available
                  </div>
                )}
              </div>
            )}

            {/* Fair Amenities (Issue #54) */}
            {(stand.has_live_music || stand.has_alcohol || stand.has_guest_appearances ||
              stand.has_giveaways || stand.has_food_trucks || stand.is_affiliated) && (
              <div className="mb-2">
                <div className="flex flex-wrap gap-1">
                  {stand.has_live_music && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" title="Live Music">
                      🎵
                    </span>
                  )}
                  {stand.has_alcohol && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title="Beer & Wine">
                      🍺
                    </span>
                  )}
                  {stand.has_guest_appearances && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-pink-100 text-pink-800" title="Guest Appearances">
                      ⭐
                    </span>
                  )}
                  {stand.has_giveaways && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800" title="Giveaways">
                      🎁
                    </span>
                  )}
                  {stand.has_food_trucks && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" title="Food Trucks">
                      🚚
                    </span>
                  )}
                  {stand.is_affiliated && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-moonbone text-ink" title="Affiliated Event">
                      🤝
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Follower Count Badge (Issue #1313) */}
            {stand.follower_count !== undefined && stand.follower_count > 0 && (
              <div className="mb-2">
                <FollowerCountBadge followerCount={stand.follower_count} size="small" />
              </div>
            )}

            <div className="space-y-1 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <MapPin className="h-4 w-4" />
                <span>{stand.address?.city || 'Unknown'}, {stand.address?.state || 'Unknown'}</span>
              </div>
              {userLocation && 'distance' in stand && (
                <div className="flex items-center space-x-1 text-secondary font-medium">
                  <MapPin className="h-4 w-4" />
                  <span>{formatDistance(stand.distance as number)}</span>
                </div>
              )}
              {stand.website && (
                <div className="flex items-center space-x-1">
                  <Globe className="h-4 w-4" />
                  <span>Visit Website</span>
                </div>
              )}
              {stand.phone && (
                <div className="flex items-center space-x-1">
                  <Phone className="h-4 w-4" />
                  <span>{stand.phone}</span>
                </div>
              )}
            </div>
          </div>
        </Link>
      );

      // Add ad after every N artist fairs (configurable)
      if (adConfig.enabled && (index + 1) % adConfig.frequency === 0 && index < regularFairs.length - 1) {
        items.push(
          <AdPlacement
            key={`ad-${index}`}
            config={adConfig}
            position="inline"
            index={index}
            className="my-4"
          />
        );
      }
    });

    return items;
  };

  // Generate dynamic SEO description based on filters
  const generateSEODescription = () => {
    let description = 'Find fresh produce, local artist fairs, and farmers markets';

    if (searchName) {
      description += ` matching "${searchName}"`;
    }

    if (selectedState) {
      description += ` in ${selectedState}`;
    }

    if (selectedCity) {
      description += `, ${selectedCity}`;
    }

    description += '. Connect directly with local farmers for organic vegetables, fresh fruits, eggs, honey, and seasonal produce.';

    return description;
  };

  const generateSEOTitle = () => {
    let title = 'Local Artist Fairs';

    if (searchName) {
      title += ` - "${searchName}"`;
    }

    if (selectedState || selectedCity) {
      title += ' in';
      if (selectedCity) title += ` ${selectedCity}`;
      if (selectedState) title += ` ${selectedState}`;
    }

    return title;
  };

  const generateSEOKeywords = () => {
    let keywords = 'artist fairs, local artist fairs, farmers market, fresh produce, organic vegetables';

    if (searchName) {
      keywords += `, ${searchName}, ${searchName} artist fairs`;
    }

    if (selectedState) {
      keywords += `, ${selectedState} artist fairs, ${selectedState} farmers market`;
    }

    if (selectedCity) {
      keywords += `, ${selectedCity} artist fairs, ${selectedCity} farmers market`;
    }

    return keywords;
  };

  // Create structured data for artist fairs
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": generateSEOTitle(),
    "description": generateSEODescription(),
    "numberOfItems": filteredFairs.length,
    "itemListElement": filteredFairs.slice(0, 10).map((stand, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LocalBusiness",
        "@id": `https://localartistfairs.com/fair/${stand.id}`,
        "name": stand.name,
        "description": stand.description || `Fresh produce and artisan products at ${stand.name}`,
        "url": `https://localartistfairs.com/fair/${stand.id}`,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": stand.address?.city,
          "addressRegion": stand.address?.state,
          "addressCountry": "US"
        },
        "image": stand.image,
        "telephone": stand.phone,
        "priceRange": "$"
      }
    }))
  };

  // Responsive layout: mobile filters at top, desktop filters in left sidebar
  return (
    <>
      <SEO
        title={generateSEOTitle()}
        description={generateSEODescription()}
        keywords={generateSEOKeywords()}
        url="https://localartistfairs.com/"
        structuredData={structuredData}
      />
      <main className="flex-grow bg-gray-50 flex flex-col min-h-0">
      {/* Mobile Filters - Pinned at top on mobile only */}
      <aside className="lg:hidden flex-shrink-0 bg-white border-b border-gray-200 z-10" aria-label="Fair filters">
        <MobileFairFilters
          selectedState={selectedState}
          selectedCity={selectedCity}
          searchName={searchName}
          selectedAttendantStatus={selectedAttendantStatus}
          radiusMiles={radiusMiles}
          maxAdmissionPrice={maxAdmissionPrice}
          selectedCategory={selectedCategory}
          onStateChange={setSelectedState}
          onCityChange={setSelectedCity}
          onSearchNameChange={setSearchName}
          onAttendantStatusChange={setSelectedAttendantStatus}
          onRadiusChange={setRadiusMiles}
          onMaxAdmissionChange={setMaxAdmissionPrice}
          onCategoryChange={setSelectedCategory}
          userLocation={userLocation}
          onLocationRequest={handleLocationRequest}
        />
      </aside>

      {/* View Toggle Tabs and Date Filter */}
      <nav className="flex-shrink-0 bg-white border-b border-gray-200" aria-label="View controls">
        <div className="px-4 py-3 space-y-3">
          {/* View Mode Toggle + Date Filter - Combined Row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* View Mode Toggle - Left Aligned */}
            <div className="flex gap-2" role="group" aria-label="View mode selection">
              <button
                onClick={() => setViewMode('list')}
                aria-label="Switch to list view"
                aria-pressed={viewMode === 'list'}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 ${
                  viewMode === 'list'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <List className="h-4 w-4" />
                <span>List View</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                aria-label="Switch to map view"
                aria-pressed={viewMode === 'map'}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 ${
                  viewMode === 'map'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Map className="h-4 w-4" />
                <span>Map View</span>
              </button>
            </div>

            {/* Date Filter Toggle - Centered on desktop, full width on mobile (Issue #39) */}
            <div className="flex flex-wrap items-center justify-center gap-2 flex-1 min-w-fit" role="group" aria-label="Date filter selection">
              <span className="text-sm font-medium text-gray-700">Show:</span>
              {([
                { value: 'upcoming' as const, label: '📅 Upcoming', ariaLabel: 'Show upcoming fairs' },
                { value: 'all' as const, label: 'All Fairs', ariaLabel: 'Show all fairs' },
                { value: 'past' as const, label: 'Past Events', ariaLabel: 'Show past events' }
              ]).map(({ value, label, ariaLabel }) => (
                <button
                  key={value}
                  onClick={() => {
                    setDateFilter(value);
                    setCurrentPage(1);
                  }}
                  aria-label={ariaLabel}
                  aria-pressed={dateFilter === value}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 ${
                    dateFilter === value
                      ? 'bg-dusk-rose text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Area - Desktop: sidebar + content, Mobile: just content */}
      <div className="flex-grow flex overflow-hidden min-h-0">
        {/* Desktop Sidebar Filters - Hidden on mobile */}
        <aside className="hidden lg:block w-80 bg-white border-r border-gray-200 flex-shrink-0" aria-label="Fair filters">
          <div className="h-full overflow-y-auto p-4">
            {/* Sort Controls (Issue #1197) - Moved here to save space */}
            <div className="mb-2 pb-2 border-b border-gray-200">
              <label htmlFor="sort-select" className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Sort by:
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                data-testid="fair-sort-select"
              >
                <option value="featured">Featured First</option>
                <option value="date">Date {dateFilter === 'past' ? '(Latest)' : '(Earliest)'}</option>
                <option value="distance" disabled={!userLocation}>
                  Distance {!userLocation && '(Enable location)'}
                </option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
            <FairFilters
              selectedState={selectedState}
              selectedCity={selectedCity}
              searchName={searchName}
              selectedAttendantStatus={selectedAttendantStatus}
              radiusMiles={radiusMiles}
              maxAdmissionPrice={maxAdmissionPrice}
              selectedCategory={selectedCategory}
              onStateChange={setSelectedState}
              onCityChange={setSelectedCity}
              onSearchNameChange={setSearchName}
              onAttendantStatusChange={setSelectedAttendantStatus}
              onRadiusChange={setRadiusMiles}
              onMaxAdmissionChange={setMaxAdmissionPrice}
              onCategoryChange={setSelectedCategory}
              userLocation={userLocation}
              onLocationRequest={handleLocationRequest}
            />
          </div>
        </aside>

        {/* Content Area - Scrollable List with Featured Artist Fairs First */}
        <section className="flex-grow overflow-y-auto" aria-label="Fair listings">
          {/* Artist Fairs List (Featured appear first with special styling) */}
          <div className="px-4 py-4">
            {loading && (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading artist fairs...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-red-500">{error}</p>
              </div>
            )}

            {!loading && !error && viewMode === 'list' && (
              <>
                <div className="space-y-4 lg:grid lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 lg:gap-4 lg:space-y-0">
                  {createFairsWithAds()}

                  {sortedFairs.length === 0 && (
                    <div className="lg:col-span-full text-center py-8">
                      <p className="text-gray-500">No artist fairs match your selected filters.</p>
                    </div>
                  )}
                </div>

                {/* Pagination Controls */}
                {sortedFairs.length > 0 && (
                  <div className="mt-6">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      totalItems={totalItems}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      pageSizeOptions={[10, 25, 50, 100]}
                      itemName="artist fairs"
                    />
                  </div>
                )}
              </>
            )}

            {!loading && !error && viewMode === 'map' && (
              <MapView fairs={sortedFairs} />
            )}

            {/* Footer Advertisement */}
            {adConfig.enabled && adConfig.footerEnabled && viewMode === 'list' && (
              <div className="mt-8 pb-4">
                <AdPlacement
                  config={adConfig}
                  position="footer"
                  className="mb-4"
                />
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
    </>
  );
}